import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DealerTimezoneService } from '@applications/sha-services';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { DeepCopyPipe, MomentPipe } from '@quorum/common-pipes';
import { CrmAppointment as Appointment, Employee } from '@quorum/models/xs-resource';
import { ActivitiesStateService } from '@quorum/xsr-salesplanner-legacy/services';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { createFormGroupFromObject } from '@applications/sha-services';
import { SystemControlState } from '@quorum/xsr-system-control';
import moment from 'moment';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';

@Component({
  selector: 'crm-appointment-card',
  templateUrl: './appointment-card.component.html',
  styleUrls: ['./appointment-card.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentCardComponent implements OnInit, OnChanges {
  @Input() appointment: Appointment;
  @Output()
  appointmentUpdated: EventEmitter<{
    appointmentCopy: Appointment;
    updatedAppointment: Appointment;
  }> = new EventEmitter<{
    appointmentCopy: Appointment;
    updatedAppointment: Appointment;
  }>();
  appointmentCopy: Appointment = new Appointment();
  appointmentForm: FormGroup;
  newTime: Date;
  systemControl$: Observable<SystemControlState>;
  currentUser$: Observable<Employee>;

  constructor(
    private activitiesStateService: ActivitiesStateService,
    private authenticationStateService: AuthenticationStateService,
    private systemControlStateService: SystemControlStateService,
    public dealerTimezoneService: DealerTimezoneService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    this.currentUser$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      take(1),
      tap((currentUser) => {
        this.systemControl$ = this.systemControlStateService
          .getSystemControlValue('CL_SALES_APPOINTMENT_CONFIRMATIONS', currentUser.rooftopId)
          .pipe(take(1));
      })
    );

    if (changes.appointment.currentValue) {
      this.dealerTimezoneService
        .convertUtcToDealerTimeZoneAsIso(changes.appointment.currentValue.appointmentDate)
        .pipe(take(1))
        .subscribe((appointmentDateFromUtc: string) => {
          this.appointmentForm = createFormGroupFromObject({
            ...changes.appointment.currentValue,
            appointmentDate: appointmentDateFromUtc,
          });

          this.appointmentForm.addControl(
            'appointmentTime',
            new FormControl(moment.parseZone(this.appointmentForm.get('appointmentDate').value).format('HH:mm'))
          );
          this.appointmentCopy = new DeepCopyPipe().transform({
            ...changes.appointment.currentValue,
            appointmentDate: appointmentDateFromUtc,
          });
          this.cdr.detectChanges();
        });
    }
  }

  updateAppointment(currentUser: Employee, dealerTimeZoneData: any): void {
    if (
      (this.appointmentForm.get('attendanceFlag').value == 'N' ||
        this.appointmentForm.get('attendanceFlag').value == 'S') &&
      this.appointmentForm.get('completedDate').value == null
    ) {
      this.appointmentForm.get('completedDate').setValue(new Date().toISOString());
    } else if (this.appointmentForm.get('attendanceFlag').value == 'null') {
      this.appointmentForm.get('attendanceFlag').setValue(null);
      this.appointmentForm.get('completedDate').setValue(null);
    }

    if (this.appointmentForm.get('confirmationFlag').value) {
      this.appointmentForm.get('confirmationDate').setValue(new Date());
      this.appointmentForm.get('confirmationAssociateId').setValue(currentUser.associateId);
    } else {
      this.appointmentForm.get('confirmationDate').setValue(null);
      this.appointmentForm.get('confirmationAssociateId').setValue(null);
    }

    const timeDate = new MomentPipe().timeDate(this.appointmentForm.get('appointmentTime').value, 'hh:mm');
    this.newTime = new Date(this.appointmentForm.get('appointmentDate').value);
    this.newTime.setHours(timeDate.getHours());
    this.newTime.setMinutes(timeDate.getMinutes());

    const utcToStore = this.dealerTimezoneService.convertBrowserTimeToDealerTime(
      this.newTime.toISOString(),
      dealerTimeZoneData.dealerTz,
      dealerTimeZoneData.daylightSavingsEnabled
    );

    this.appointmentForm.get('appointmentDate').setValue(utcToStore);

    this.activitiesStateService.updateAppointmentOnServer(this.appointmentCopy, this.appointmentForm.value);
    this.appointmentUpdated.emit({
      appointmentCopy: this.appointmentCopy,
      updatedAppointment: this.appointmentForm.value,
    });
  }
}
